import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import ProductCard from '../components/ProductCard';
import Products2Hero from '../components/Products2Hero';
import { generateProductSlug } from '../utils/seoUtils';
import './LaptopsPage.css';

const MiniPCsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const [miniPCs, setMiniPCs] = useState([]);
  const [filteredMiniPCs, setFilteredMiniPCs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  
  // Filter states - updated for mini PCs
  const [filters, setFilters] = useState({
    availability: '',
    priceRange: '',
    brand: '',
    processor: '',
    generation: '',
    ram: '',
    hardDiskSize: '',
    category: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    fetchMiniPCs();
  }, []);

  useEffect(() => {
    // Check for category and brand parameters in URL
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const brandParam = urlParams.get('brand');
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      setFilters(prev => ({
        ...prev,
        category: categoryParam
      }));
    }
    
    if (brandParam) {
      setSelectedBrand(brandParam);
      setFilters(prev => ({
        ...prev,
        brand: brandParam
      }));
    }
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [miniPCs, filters, sortBy]);

  const fetchMiniPCs = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products', 'minipc', 'items'));
      const miniPCData = querySnapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        const id = docSnapshot.id;
        const slug = generateProductSlug({ id, ...data });
        
        if (!data.slug || data.slug !== slug) {
          updateDoc(doc(db, 'products', 'minipc', 'items', id), { slug })
            .catch(err => console.warn('Mini PC slug update failed:', err));
        }
        
        return { id, ...data, slug };
      });
      setMiniPCs(miniPCData);
    } catch (err) {
      setError('Failed to load mini PCs');
      console.error('Error fetching mini PCs:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...miniPCs];

    // Apply filters
    if (filters.category) {
      filtered = filtered.filter(miniPC => miniPC.category === filters.category);
    }
    if (filters.availability) {
      filtered = filtered.filter(miniPC => miniPC.availability === filters.availability);
    }
    if (filters.brand) {
      filtered = filtered.filter(miniPC => miniPC.brand === filters.brand);
    }
    if (filters.processor) {
      filtered = filtered.filter(miniPC => miniPC.processor.toLowerCase().includes(filters.processor.toLowerCase()));
    }
    if (filters.generation) {
      filtered = filtered.filter(miniPC => miniPC.generation === filters.generation);
    }
    if (filters.ram) {
      filtered = filtered.filter(miniPC => miniPC.ramSize === filters.ram);
    }
    if (filters.hardDiskSize) {
      filtered = filtered.filter(miniPC => miniPC.storageSize === filters.hardDiskSize);
    }
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(miniPC => {
        const price = miniPC.price;
        if (max) return price >= min && price <= max;
        return price >= min;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      default:
        // Featured - keep original order
        break;
    }

    setFilteredMiniPCs(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      availability: '',
      priceRange: '',
      brand: '',
      processor: '',
      generation: '',
      ram: '',
      hardDiskSize: '',
      category: ''
    });
    setSelectedCategory('');
    setSelectedBrand('');
    // Clear URL parameters
    navigate('/mini-pcs', { replace: true });
  };

  const handleProductClick = (miniPC) => {
    const slug = generateProductSlug(miniPC);
    navigate(`/mini-pc/${slug || miniPC.id}`);
  };

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      imageUrls: product.imageUrls,
      features: [product.processor, product.ramSize, `${product.storageSize || ''} ${product.storageType || ''}`.trim()].filter(Boolean),
      quantity: 1
    };

    addToCart(cartItem);
    soundEffects.playAddToCartSound();
  };

  // Get unique filter options
  const getUniqueValues = (key) => {
    return [...new Set(miniPCs.map(miniPC => miniPC[key]).filter(Boolean))];
  };

  return (
    <div className="laptops-page">
      <Products2Hero 
        className="hero-tight"
        tagline="COMPACT POWER · CERTIFIED REFURBISHED · 12-MONTH WARRANTY"
        title1="Mini PCs"
        title2=""
        title3=""
        description=""
        button1Text="Shop Mini PCs"
        button1Link="#laptops-collection"
      />
      <div className="container" id="laptops-collection">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading mini PCs...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Filter and Sort Section */}
            <div className="filter-section">
              <div className="filter-header">
                <div className="results-info">
                  <h2>
                    Showing {filteredMiniPCs.length} {selectedBrand ? selectedBrand : selectedCategory ? selectedCategory.toLowerCase() : ''} mini PCs
                  </h2>
                  <p>
                    {selectedBrand 
                      ? `Perfect compact ${selectedBrand} systems for your needs`
                      : selectedCategory 
                        ? `Perfect compact ${selectedCategory.toLowerCase()} systems for your needs`
                        : 'Find your perfect compact system'
                    }
                  </p>
                </div>
                <div className="filter-controls">
                  <button 
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                    </svg>
                    Filters
                  </button>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest First</option>
                    <option value="discount">Best Deals</option>
                  </select>
                </div>
              </div>

              {/* Filter Panel */}
              <div className={`filter-panel ${showFilters ? 'show' : ''}`}>
                <div className="filter-grid">
                  <div className="filter-group">
                    <label>Availability</label>
                    <select 
                      value={filters.availability} 
                      onChange={(e) => handleFilterChange('availability', e.target.value)}
                    >
                      <option value="">All Availability</option>
                      {getUniqueValues('availability').map(availability => (
                        <option key={availability} value={availability}>{availability}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Price Range</label>
                    <select 
                      value={filters.priceRange} 
                      onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    >
                      <option value="">All Prices</option>
                      <option value="0-20000">Under ₹20,000</option>
                      <option value="20000-40000">₹20,000 - ₹40,000</option>
                      <option value="40000-60000">₹40,000 - ₹60,000</option>
                      <option value="60000-80000">₹60,000 - ₹80,000</option>
                      <option value="80000">Above ₹80,000</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Brand</label>
                    <select 
                      value={filters.brand} 
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                    >
                      <option value="">All Brands</option>
                      {getUniqueValues('brand').map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Processor</label>
                    <select 
                      value={filters.processor} 
                      onChange={(e) => handleFilterChange('processor', e.target.value)}
                    >
                      <option value="">All Processors</option>
                      <option value="Intel Core i3">Intel Core i3</option>
                      <option value="Intel Core i5">Intel Core i5</option>
                      <option value="Intel Core i7">Intel Core i7</option>
                      <option value="AMD Ryzen">AMD Ryzen</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Generation</label>
                    <select 
                      value={filters.generation} 
                      onChange={(e) => handleFilterChange('generation', e.target.value)}
                    >
                      <option value="">All Generations</option>
                      {getUniqueValues('generation').map(generation => (
                        <option key={generation} value={generation}>{generation}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>RAM</label>
                    <select 
                      value={filters.ram} 
                      onChange={(e) => handleFilterChange('ram', e.target.value)}
                    >
                      <option value="">All RAM</option>
                      {getUniqueValues('ramSize').map(ram => (
                        <option key={ram} value={ram}>{ram}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Hard Disk Size</label>
                    <select 
                      value={filters.hardDiskSize} 
                      onChange={(e) => handleFilterChange('hardDiskSize', e.target.value)}
                    >
                      <option value="">All Storage</option>
                      {getUniqueValues('storageSize').map(storage => (
                        <option key={storage} value={storage}>{storage}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-actions">
                    <button onClick={clearFilters} className="clear-filters">
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="laptops-grid">
              {filteredMiniPCs.map((miniPC) => (
                <div key={miniPC.id} className="laptop-card-wrapper">
                  <ProductCard
                    product={{
                      image: miniPC.imageUrls?.[0] || '/api/placeholder/300/200',
                      imageAlt: miniPC.name,
                      badge: miniPC.availability === 'Available' ? 'In Stock' : 'Out of Stock',
                      isNew: false,
                      stockLeft: miniPC.stockLeft,
                      brand: miniPC.brand || 'NexaWave',
                      name: miniPC.name,
                      subtitle: [miniPC.condition, miniPC.warranty].filter(Boolean).join(' | '),
                      scentNote: [miniPC.processor, miniPC.ramSize, [miniPC.storageSize, miniPC.storageType].filter(Boolean).join(' ')].filter(Boolean).join(' | '),
                      rating: Number(miniPC.rating) || 4.8,
                      reviews: Number(miniPC.reviews) || 120,
                      originalPrice: Number(miniPC.originalPrice) || Number(miniPC.price) || 0,
                      salePrice: Number(miniPC.price) || Number(miniPC.originalPrice) || 0
                    }}
                    onCardClick={() => handleProductClick(miniPC)}
                    onAddToCart={() => handleAddToCart(miniPC)}
                    canAddToCart={miniPC.availability === 'Available'}
                    showRating={false}
                    titleTwoLine={true}
                  />
                </div>
              ))}
            </div>

            {filteredMiniPCs.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No mini PCs found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MiniPCsPage;